import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { User } from 'oidc-client-ts';
import { userManager } from './oidcClient';

export interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: Error | null;
  roles: string[];
  traegerIds: string[];
  accessToken: string | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface ProfileClaims {
  realm_access?: { roles?: string[] };
  resource_access?: Record<string, { roles?: string[] }>;
  traeger_ids?: string[];
}

/** Decode a JWT payload (ohne Signaturprüfung — die Validierung übernimmt das Backend). */
function decodeJwt(token: string | undefined): ProfileClaims | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    const padded = parts[1] + '='.repeat((4 - (parts[1].length % 4)) % 4);
    const json = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as ProfileClaims;
  } catch {
    return null;
  }
}

/**
 * Liefert die Rollen des Users.
 *
 * Reihenfolge der Quellen (Keycloak schreibt `realm_access.roles` per Default
 * NUR ins Access-Token, nicht in den ID-Token):
 *   1. profile (= ID-Token), falls Roles-Mapper auch dort liefert,
 *   2. Access-Token-Payload als Fallback.
 */
function extractRoles(user: User | null): string[] {
  if (!user) return [];
  const sources: ProfileClaims[] = [];
  sources.push(user.profile as unknown as ProfileClaims);
  const fromAccess = decodeJwt(user.access_token);
  if (fromAccess) sources.push(fromAccess);

  const out = new Set<string>();
  for (const c of sources) {
    for (const r of c.realm_access?.roles ?? []) out.add(r);
    for (const sub of Object.values(c.resource_access ?? {})) {
      for (const r of sub.roles ?? []) out.add(r);
    }
  }
  return [...out];
}

function extractTraegerIds(user: User | null): string[] {
  if (!user) return [];
  const profile = user.profile as unknown as ProfileClaims;
  if (profile.traeger_ids?.length) return profile.traeger_ids;
  const fromAccess = decodeJwt(user.access_token);
  return fromAccess?.traeger_ids ?? [];
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const refresh = useCallback(async () => {
    try {
      const u = await userManager.getUser();
      setUser(u && !u.expired ? u : null);
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const u = await userManager.getUser();
        if (!cancelled) setUser(u && !u.expired ? u : null);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e : new Error(String(e)));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    const onLoaded = (u: User) => setUser(u);
    const onUnloaded = () => setUser(null);
    userManager.events.addUserLoaded(onLoaded);
    userManager.events.addUserUnloaded(onUnloaded);
    return () => {
      cancelled = true;
      userManager.events.removeUserLoaded(onLoaded);
      userManager.events.removeUserUnloaded(onUnloaded);
    };
  }, []);

  const login = useCallback(async () => {
    await userManager.signinRedirect();
  }, []);

  const logout = useCallback(async () => {
    await userManager.signoutRedirect();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading,
      isAuthenticated: !!user && !user.expired,
      error,
      roles: extractRoles(user),
      traegerIds: extractTraegerIds(user),
      accessToken: user?.access_token ?? null,
      login,
      logout,
      refresh,
    }),
    [user, isLoading, error, login, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
